import {
  PrimaryFocus,
  CoOccurringDisorder,
  PRIMARY_FOCUS_SLUG_MAPPING
} from '@/domain/primaryFocus/constant'
import IconBedPlus from '@/components/icons/IconBedPlus/IconBedPlus'
import IconHouse from '@/components/icons/IconHouse/IconHouse'
import IconChair from '@/components/icons/IconChair/IconChair'
import IconHome from '@/components/icons/IconHome/IconHome'
import IconLaptop from '@/components/icons/IconLaptop/IconLaptop'

export const LEVELS_OF_CARE_FILTERS = [
  {
    name: 'Detox',
    label: 'Detox',
    image:
      'https://res.cloudinary.com/rehabpath/image/upload/w_100,h_100,q_60/v1698180365/levels-of-care-detox.jpg',
    icon: IconBedPlus
  },
  {
    name: 'Residential',
    label: 'Residential',
    image:
      'https://res.cloudinary.com/rehabpath/image/upload/w_100,h_100,q_60/v1698180369/levels-of-care-residential.jpg',
    icon: IconHouse
  },
  {
    name: 'Outpatient',
    label: 'Outpatient',
    image:
      'https://res.cloudinary.com/rehabpath/image/upload/w_100,h_100,q_60/v1698180365/levels-of-care-outpatient.jpg',
    icon: IconChair
  },
  {
    name: 'Sober Living',
    label: 'Sober Living',
    image:
      'https://res.cloudinary.com/rehabpath/image/upload/w_100,h_100,q_60/v1698180365/levels-of-care-sober-living.jpg',
    icon: IconHome
  },
  {
    name: 'Virtual',
    label: 'Virtual',
    image:
      'https://res.cloudinary.com/rehabpath/image/upload/w_100,h_100,q_60/v1698180364/levels-of-care-virtual.jpg',
    icon: IconLaptop
  }
]

export const CARE_OPTIONS_DATA = {
  [PrimaryFocus.SUBSTANCE_USE]: {
    description:
      'Everyone’s recovery journey is unique. Whether it looks like residential rehab for 30 days, an outpatient program, or virtual therapy, you can find a level of care that fits your needs.',
    levels: [
      { level: 'Detox' },
      { level: 'Concierge Treatment' },
      { level: 'Residential' },
      { level: 'Retreat' },
      { level: 'Sober Living' },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      { level: CoOccurringDisorder.MENTAL_HEALTH },
      { level: 'Interventionists' }
    ]
  },
  [PrimaryFocus.MENTAL_HEALTH]: {
    description:
      'Mental health care includes a range of services such as counseling, therapy, medication management, and support groups, all designed to address issues from everyday stress and anxiety to more severe conditions like depression and schizophrenia. These practices are essential for helping individuals manage their emotions, improve their relationships, and navigate life’s challenges in healthier, more adaptive ways.',
    linkText: (location) => `Mental health treatment centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/depression/`,
    levels: [
      { level: 'Concierge Treatment' },
      {
        level: 'Residential',
        description:
          'Intensive monitoring and care for conditions like depression, trauma, suicidality, and anxiety. Residential mental health facilities offer a safe setting with activities and structure, plus opportunities to practice life skills and bond with peers.'
      },
      { level: 'Retreat' },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      {
        level: 'Licensed Primary Mental Health',
        description:
          'Licensed primary mental health facilities provide specialized care for diagnosing and treating mental health conditions. By offering early intervention and comprehensive services, they promote overall well-being and prevent long-term complications.'
      },
      {
        level: CoOccurringDisorder.SUBSTANCE_USE,
        description:
          'Substance use disorders often occur alongside primary mental health conditions. Programs for co-occurring substance use disorders focus on both conditions, tailoring treatment for comprehensive recovery.'
      },
      { level: 'Interventionists' }
    ]
  },
  [PrimaryFocus.SUBSTANCE_USE_AND_MENTAL_HEALTH]: {
    description:
      'Substance use and mental health issues can occur simultaneously or as primary conditions. Effective treatment explores how mental health conditions contribute to addiction, and vice-versa. Many rehabs treat substance use disorders and mental health conditions with personalized care plans.',
    levels: [
      {
        level: 'Detox',
        description:
          'Typically the initial stage of treatment for substance use, focusing on medically supervised management of withdrawal symptoms.'
      },
      { level: 'Concierge Treatment' },
      {
        level: 'Residential',
        description:
          'Residential programs for substance use and mental health with structured care and 24/7 monitoring. Treatment takes place on-site, with behavioral health professionals often providing care in group, individual, and family settings.'
      },
      { level: 'Retreat' },
      {
        level: 'Sober Living',
        description:
          'Transitional housing designed to support individuals recovering from substance use disorders offering a safe, supportive and structured environment for practicing long-term sobriety, while reintegrating back into daily living.'
      },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      { level: 'Licensed Primary Mental Health' },
      {
        level: 'Co-Occurring Disorders',
        description:
          'Substance use and mental health can occur simultaneously as co-occurring disorders. Treatment for co-occurring disorders involves therapy and other personalized interventions to address both conditions.'
      },
      { level: 'Interventionists' }
    ]
  },
  [PrimaryFocus.EATING_DISORDERS]: {
    description:
      'Eating disorders interfere with healthy eating habits, potentially causing weight changes, poor mental health, and physical harm. Treatment addresses underlying thoughts and beliefs, aiming to heal relationships with food. Weight restoration and other medical interventions stabilize physical health.',
    linkText: (location) => `Eating disorders in ${location}`,
    url: (locationSlug) => `/${locationSlug}/eating-disorders/`,
    levels: [
      { level: 'Concierge Treatment' },
      {
        level: 'Residential',
        description:
          'Residential eating disorder treatment provides intensive medical and therapeutic care in a safe, structured setting with 24/7 monitoring, meal-time support, therapy, and services for weight restoration as needed.'
      },
      { level: 'Retreat' },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      {
        level: 'Co-Occurring Disorders',
        description:
          'Conditions like depression, substance use, and trauma can co-occur with eating disorders. Many programs provide co-occurring eating disorder treatment for comprehensive healing.'
      }
    ]
  },
  [PrimaryFocus.BEHAVIORAL_ADDITION]: {
    description:
      'Behavioral addictions cause repeated compulsory engagement in pleasurable activities, such as gambling, despite adverse consequences. Treatment typically involves therapy and psychoeducation to help individuals understand and modify their behaviors while addressing underlying psychological factors.',
    levels: [
      { level: 'Concierge Treatment' },
      {
        level: 'Residential',
        description:
          'Structured, live-in care for behavioral addiction treatment with various therapies to address underlying concerns, build new habits, and learn coping tools in group, 1:1, and family settings.'
      },
      { level: 'Retreat' },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      {
        level: 'Co-Occurring Disorders',
        description:
          'Behavioral addictions can occur alongside a mental health condition or substance use disorder. Treatment for co-occurring behavioral addictions uses therapy and skill-building to teach new coping tools and address co-occurring disorders.'
      }
    ]
  },
  [PrimaryFocus.WELLNESS]: {
    description:
      'Wellness focuses on whole-person health and practical strategies for improving well-being. Nutrition, fitness, fun activities, and relaxation practices improve overall wellness. Rehabs offer wellness services to aid in comprehensive healing from addiction, mental health conditions, and burnout.',
    levels: [
      { level: 'Concierge Treatment' },
      {
        level: 'Residential',
        description:
          'Intensive residential treatment in a soothing space with relaxing amenities and activities focused on holistic health, whole-person wellness, and restorative rest.'
      },
      { level: 'Retreat' },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      {
        level: 'Co-Occurring Disorders',
        description:
          'Wellness retreats and programs address co-occurring disorders through soothing activities, mind-body-spirit treatments, and restorative amenities for rest and relaxation.'
      }
    ]
  },
  [PrimaryFocus.AUTISM]: {
    description:
      'Autism is a developmental condition affecting social skills and interactions. Personalized treatment for autism addresses where people fall on the spectrum, improving confidence in social settings and teaching practical skills for communication and daily life activities.',
    levels: [
      { level: 'Concierge Treatment' },
      {
        level: 'Residential',
        description:
          'In residential treatment for autism, experienced behavioral health practitioners monitor day-to-day treatment and activities, providing therapy, life-skills practice, and 24/7 support in a structured environment.'
      },
      { level: 'Retreat' },
      { level: 'Recovery Coaching' },
      { level: 'Day Treatment' },
      { level: 'Intensive Outpatient Program' },
      { level: 'Outpatient' },
      { level: 'Virtual & In-Home Care' },
      { level: 'Intensive Inpatient' },
      {
        level: 'Co-Occurring Disorders',
        description:
          "Autism can occur alongside mental health or substance use disorders as a co-occurring condition. Treatment for co-occurring disorders with autism includes care personalized to each patient's needs and where they fall on the spectrum of autism."
      }
    ]
  }
}

export const PRIMARY_FOCUS_ANCHOR_TEXT_MAPPING = {
  [PrimaryFocus.SUBSTANCE_USE]: `Rehabs`,
  [PrimaryFocus.MENTAL_HEALTH]: `Mental health treatment centers`,
  [PrimaryFocus.EATING_DISORDERS]: `Eating disorder centers`
}

export const LEVELS_OF_CARE_SECTIONS = {
  Detox: {
    required: true,
    description:
      'Typically the initial stage of treatment for substance use, focusing on medically supervised management of withdrawal symptoms.',
    linkText: (location) => `Detox centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/detox/`
  },
  'Concierge Treatment': {
    description:
      'Concierge treatment offers highly individualized care and direct access to medical professionals. This model emphasizes convenience, extensive access, and tailored health management, often involving a more holistic and 1:1 approach to healing.',
    linkText: () => 'Centers offering concierge treatment',
    url: () => '/care/concierge-treatment/'
  },
  Residential: {
    aliases: ['Residential', 'Residential Rehab'],
    required: true,
    description:
      'Offering intensive care with 24/7 monitoring, residential substance use treatment is typically 30 days. Length can range from 14 to 90 days.',
    linkText: (location, primaryFocus) =>
      `${
        PRIMARY_FOCUS_ANCHOR_TEXT_MAPPING[primaryFocus] ?? 'Residential centers'
      } in ${location}`,
    url: (locationSlug, primaryFocus) =>
      PRIMARY_FOCUS_SLUG_MAPPING[primaryFocus]
        ? `/${locationSlug}/${PRIMARY_FOCUS_SLUG_MAPPING[primaryFocus]}/`
        : `/${locationSlug}/`
  },
  Retreat: {
    description:
      'Specialized facilities often located in tranquil, secluded environments where patients focus intensively on their recovery. Holistic treatment programs blend physical, mental, and sometimes spiritual healing processes for overall wellness and recovery.',
    linkText: () => `Mental health retreats`,
    url: () => `/care/retreat/`
  },
  'Co-Occurring Mental Health': {
    description:
      'Treatment for co-occurring mental health disorders, which affect the majority of individuals diagnosed, integrates care for mental health conditions with primary substance use. Personalized care plans are crafted to effectively address each condition simultaneously, optimizing the healing process.',
    linkText: (location) => `Dual diagnosis treatment centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/co-occurring-disorders/`
  },
  'Co-Occurring Substance Use': {
    description:
      'Substance use disorders can occur alongside primary mental health conditions. Programs for co-occurring substance use disorders focus on both conditions, tailoring treatment for comprehensive recovery.',
    linkText: (location) => `Dual diagnosis treatment centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/co-occurring-disorders/`
  },
  'Co-Occurring Disorders': {
    description:
      'Substance use and mental health can occur simultaneously as co-occurring disorders. Treatment for co-occurring disorders involves therapy and other personalized interventions to address both conditions.',
    linkText: (location) => `Dual diagnosis treatment centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/co-occurring-disorders/`
  },
  'Sober Living': {
    required: true,
    description:
      'Transitional housing designed to support individuals recovering from substance use disorders offering a safe, supportive and structured environment for practicing long-term sobriety, while reintegrating back into daily living.',

    linkText: (location) => `Sober living homes in ${location}`,
    url: (locationSlug) => `/${locationSlug}/sober-living/`
  },
  'Recovery Coaching': {
    description:
      'Recovery coaching is a type of support service designed to help individuals maintain their recovery goals, provide guidance and support, and connect them with resources and community-based services.',
    linkText: () => `Recovery coaching`,
    url: () => `/care/recovery-coaching/`
  },
  'Day Treatment': {
    description:
      'In a PHP, patients live at home but follow an intensive schedule of treatment. Most programs require you to be on-site for about 40 hours per week.',
    linkText: (location) => `Day treatment centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/outpatient/`
  },
  'Intensive Outpatient Program': {
    description:
      'An intensive outpatient program (IOP) falls between inpatient care and traditional outpatient services, with therapy and skills-building activities designed to support recovery and provide flexibility. IOP is ideal for those needing support but not the 24/7 care of a residential facility.',
    linkText: (location) => `Intensive outpatient centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/intensive-outpatient-program/`
  },
  Outpatient: {
    aliases: ['Outpatient Program'],
    required: true,
    description:
      'Outpatient treatment offers flexible therapeutic and medical care without the need to stay overnight in a hospital or inpatient facility. Outpatient care typically offers a range of therapies and medical interventions individuals can attend alongside daily life.',
    linkText: (location) => `Outpatient treatment in ${location}`,
    url: (locationSlug) => `/${locationSlug}/outpatient/`
  },
  'Virtual & In-Home Care': {
    aliases: ['Virtual', 'In-Home'],
    required: true,
    description:
      'The delivery of therapeutic services utilizing technology such as video conferencing, online messaging or phone calls, allowing for flexibility, comfort and increased access to care.',
    linkText: () => `Online treatment centers`,
    url: () => `/care/virtual/`
  },
  'Intensive Inpatient': {
    description:
      'The highest level of care, medically managed with 24-hour nursing and physician care in a residential or medically-based setting, and can include any combination of medication, counseling and therapy.',
    linkText: (location) => `Intensive inpatient centers in ${location}`,
    url: (locationSlug) => `/${locationSlug}/inpatient/`
  },
  'Licensed Primary Mental Health': {
    description:
      'Some primary care providers offer mental health diagnosis and treatment. This can prevent patients from developing more serious conditions.'
  },
  'Recovery School': {
    description:
      'A school designed to support students recovering from substance use disorder or other mental/behavioral conditions that offers a combination of academic, socioemotional and therapeutic support.'
  },
  'Therapeutic Boarding School': {
    description:
      'A residential school that combines traditional academic curriculum with therapeutic services, helping students with mental/behavioral health issues, and/or addiction.'
  },
  Interventionists: {
    description:
      'Professionals who helps guide loved ones through an intervention to help someone overcome addiction or mental health issues. They may also help clients find treatment centers and navigate the recovery process.',
    linkText: () => `Interventionist services`,
    url: () => `/care/interventionists/`
  }
}
